import React, { useState, useRef, useEffect } from "react";
import styles from "../../styles/_containers/_automations/automationEditor.module.scss";

const VARIABLES = [
  { key: "שם_לקוח", label: "שם לקוח" },
  { key: "שם_עסק", label: "שם עסק" },
  { key: "שירות", label: "שירות" },
  { key: "תאריך", label: "תאריך" },
  { key: "שעה", label: "שעה" },
  { key: "ימים_שעברו", label: "ימים שעברו" },
  { key: "שם_עובד", label: "שם עובד" },
  { key: "תאריך_תפוגה", label: "תאריך תפוגה" },
];

const CONDITION_LABELS = {
  days: "ימים",
  hours: "שעות",
  uses: "שימושים",
  cancellations: "ביטולים",
  bookings: "הזמנות",
};

const ACTION_LABELS = {
  send_message: "שליחת הודעה",
  notify_business: "התראה לבעל העסק",
  tag_customer: "תיוג לקוח",
};

const MessageIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5.5h14v10H9l-4 3v-13Z" /></svg>;
const BellIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 8h18c0-1-3-1-3-8ZM10 21h4" /></svg>;
const TagIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 4 7 .2 8.8 8.8-6.8 6.8L4.2 11 4 4Z"/><circle cx="8" cy="8" r="1.2" /></svg>;

const ActionIcon = ({ type }) => {
  if (type === "notify_business") return <BellIcon />;
  if (type === "tag_customer") return <TagIcon />;
  return <MessageIcon />;
};

export default function AutomationEditor({ automation, onClose, onSave, saving }) {
  const [actions, setActions] = useState(automation?.actions || []);
  const [conditionValue, setConditionValue] = useState(automation?.conditionValue ?? "");
  const [conditionUnit] = useState(automation?.conditionUnit || "days");
  const [delay, setDelay] = useState(automation?.delay || 0);
  const [cooldownDays, setCooldownDays] = useState(automation?.cooldownDays || 0);
  const [scheduleFrom, setScheduleFrom] = useState(automation?.scheduleWindow?.from ?? 8);
  const [scheduleTo, setScheduleTo] = useState(automation?.scheduleWindow?.to ?? 21);
  const [activeActionIdx, setActiveActionIdx] = useState(0);
  const textareaRef = useRef(null);

  useEffect(() => {
    const handleEsc = event => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const msgActionIdx = actions.findIndex(action => action.type === "send_message");
  const currentAction = actions[activeActionIdx] || actions[0];

  function updateAction(idx, updates) {
    setActions(previous => previous.map((action, index) => index === idx ? { ...action, ...updates } : action));
  }

  function insertVariable(key) {
    if (msgActionIdx < 0) return;
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = actions[msgActionIdx].content || "";
    const insert = `{{${key}}}`;
    updateAction(msgActionIdx, { content: text.slice(0, start) + insert + text.slice(end) });
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + insert.length;
    }, 0);
  }

  function handleSave() {
    onSave({
      actions,
      conditionValue: conditionValue !== "" ? Number(conditionValue) : null,
      delay: Number(delay),
      cooldownDays: Number(cooldownDays),
      scheduleWindow: { from: Number(scheduleFrom), to: Number(scheduleTo) },
    });
  }

  const msgContent = msgActionIdx >= 0 ? actions[msgActionIdx].content || "" : "";
  const channel = msgActionIdx >= 0 ? actions[msgActionIdx].channel : null;
  const charLimit = channel === "sms" ? 160 : 1024;
  const isOverLimit = msgContent.length > charLimit;
  const saveDisabled = saving || (msgActionIdx >= 0 && (!msgContent.trim() || isOverLimit));

  return (
    <div className={styles.overlay} onClick={onClose}>
      <section
        className={styles.modal}
        onClick={event => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="automation-editor-title"
      >
        <header className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <div className={styles.titleIcon}><ActionIcon type={currentAction?.type} /></div>
            <div>
              <span className={styles.headerEyebrow}>עריכת אוטומציה</span>
              <h2 id="automation-editor-title" className={styles.typeName}>{automation?.name || "אוטומציה"}</h2>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="סגירת העורך">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
          </button>
        </header>

        <div className={styles.modalBody}>
          {automation?.conditionUnit && automation.conditionUnit !== "null" && (
            <section className={styles.panel}>
              <div className={styles.panelHeading}>
                <span className={styles.stepNumber}>1</span>
                <div><h3>מתי האוטומציה תפעל?</h3><p>הגדירו את התנאי שמפעיל אותה.</p></div>
              </div>
              <label className={styles.conditionControl}>
                <span>לאחר</span>
                <input
                  type="number"
                  min={1}
                  value={conditionValue}
                  onChange={event => setConditionValue(event.target.value)}
                  className={styles.numberInput}
                  aria-label="ערך התנאי"
                />
                <strong>{CONDITION_LABELS[conditionUnit] || conditionUnit}</strong>
              </label>
            </section>
          )}

          <section className={styles.panel}>
            <div className={styles.panelHeading}>
              <span className={styles.stepNumber}>{automation?.conditionUnit && automation.conditionUnit !== "null" ? "2" : "1"}</span>
              <div><h3>מה יקרה?</h3><p>בחרו פעולה וערכו את התוכן שלה.</p></div>
            </div>

            <div className={styles.actionTabs} role="tablist" aria-label="פעולות האוטומציה">
              {actions.map((action, idx) => (
                <button
                  key={`${action.type}-${idx}`}
                  className={`${styles.actionTab} ${activeActionIdx === idx ? styles.actionTabActive : ""}`}
                  onClick={() => setActiveActionIdx(idx)}
                  type="button"
                  role="tab"
                  aria-selected={activeActionIdx === idx}
                >
                  <ActionIcon type={action.type} />
                  <span>{ACTION_LABELS[action.type] || action.type}</span>
                </button>
              ))}
            </div>

            {currentAction?.type === "send_message" && (
              <div className={styles.actionEditor}>
                <span className={styles.fieldLabel}>ערוץ שליחה</span>
                <div className={styles.channelToggle}>
                  <button
                    className={`${styles.channelBtn} ${currentAction.channel === "whatsapp" ? styles.whatsappActive : ""}`}
                    onClick={() => updateAction(activeActionIdx, { channel: "whatsapp" })}
                    type="button"
                    aria-pressed={currentAction.channel === "whatsapp"}
                  >WhatsApp</button>
                  <button
                    className={`${styles.channelBtn} ${currentAction.channel === "sms" ? styles.smsActive : ""}`}
                    onClick={() => updateAction(activeActionIdx, { channel: "sms" })}
                    type="button"
                    aria-pressed={currentAction.channel === "sms"}
                  >SMS</button>
                </div>

                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>תוכן ההודעה</span>
                  <textarea
                    ref={textareaRef}
                    className={`${styles.textarea} ${isOverLimit ? styles.textareaError : ""}`}
                    dir="rtl"
                    value={currentAction.content || ""}
                    onChange={event => updateAction(activeActionIdx, { content: event.target.value })}
                    rows={6}
                    placeholder="כתבו כאן את ההודעה שתישלח ללקוח..."
                  />
                </label>
                <div className={styles.charRow}>
                  <span className={`${styles.charCount} ${isOverLimit ? styles.charOver : ""}`}>
                    {msgContent.length} / {charLimit} תווים {isOverLimit && "- חורג מהמגבלה"}
                  </span>
                </div>

                <div className={styles.variableGroup}>
                  <div><span className={styles.fieldLabel}>הוספת מידע אישי</span><small>לחיצה מוסיפה את המשתנה במקום הסמן</small></div>
                  <div className={styles.variablesRow}>
                    {VARIABLES.map(variable => (
                      <button key={variable.key} className={styles.variableBtn} onClick={() => insertVariable(variable.key)} type="button">
                        + {variable.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {currentAction?.type === "notify_business" && (
              <div className={styles.actionEditor}>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>תוכן ההתראה לעסק</span>
                  <textarea
                    className={styles.textarea}
                    dir="rtl"
                    value={currentAction.content || ""}
                    onChange={event => updateAction(activeActionIdx, { content: event.target.value })}
                    rows={4}
                    placeholder="מה תרצו שיופיע בהתראה?"
                  />
                </label>
              </div>
            )}

            {currentAction?.type === "tag_customer" && (
              <div className={styles.actionEditor}>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>שם התגית</span>
                  <input
                    className={styles.textInput}
                    dir="rtl"
                    value={currentAction.tagValue || ""}
                    onChange={event => updateAction(activeActionIdx, { tagValue: event.target.value })}
                    placeholder="לדוגמה: לקוח חוזר"
                  />
                </label>
              </div>
            )}
          </section>

          <details className={styles.advanced}>
            <summary>
              <span>הגדרות מתקדמות</span>
              <small>השהיה, מרווח בין שליחות ושעות פעילות</small>
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m6.5 8 3.5 3.5L13.5 8" /></svg>
            </summary>
            <div className={styles.advancedBody}>
              <label className={styles.advancedField}>
                <span>השהיה לפני ביצוע</span>
                <div><input type="number" min={0} value={delay} onChange={event => setDelay(event.target.value)} /><em>דקות</em></div>
              </label>
              <label className={styles.advancedField}>
                <span>מרווח בין שליחות לאותו לקוח</span>
                <div><input type="number" min={0} value={cooldownDays} onChange={event => setCooldownDays(event.target.value)} /><em>ימים</em></div>
              </label>
              <div className={styles.advancedField}>
                <span>שעות שבהן מותר לשלוח</span>
                <div className={styles.timeRange}>
                  <input aria-label="שעת התחלה" type="number" min={0} max={23} value={scheduleFrom} onChange={event => setScheduleFrom(event.target.value)} />
                  <em>עד</em>
                  <input aria-label="שעת סיום" type="number" min={0} max={23} value={scheduleTo} onChange={event => setScheduleTo(event.target.value)} />
                </div>
              </div>
            </div>
          </details>
        </div>

        <footer className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose} type="button">ביטול</button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saveDisabled} type="button">
            {saving ? <><span className={styles.savingSpinner} /> שומר שינויים...</> : "שמירת השינויים"}
          </button>
        </footer>
      </section>
    </div>
  );
}
